import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createCompany,
  fetchCompanies,
  fetchSectors,
  formatMoney,
  type ExcelImportResult,
} from '../../../../services/adminService';
import { AdminDevTh, AdminTableWrap, btnLink, btnPrimary, btnSecondary, tdClass, thClass } from '../adminUi';
import { todayIso } from '../adminShared';
import type { RecordExcelUploadDraft } from '../RecordExcelUploadModal';
import {
  canAddCompanyFromExcel,
  collectCompanyLinkTargets,
  companyLinkForRow,
  isCompanyLinkError,
  resolveCompanyLinks,
  type CompanyLinkResolution,
} from './recordExcelCompanyResolve';
import { fetchGradingTree } from '../../../../services/gradingService';
import { normalizeGradingTreePayload } from '../grading/utils';
import type { GradingTreePayload } from '../grading/types';
import { buildExcelImportRows, importExcelRowsInBatches } from './recordExcelImport';
import {
  isRoleResolutionError,
  resolveRoleRows,
  roleResolutionForRow,
  type RoleRowResolution,
} from './recordExcelRoleResolve';
import {
  RECORD_EXCEL_COLUMN_PREVIEW,
  RECORD_EXCEL_CONTEXT_FIELDS,
  RECORD_EXCEL_MAPPED_COLUMNS,
  type RecordExcelParseResult,
  type RecordExcelParsedRow,
  type RecordExcelParsedValues,
  formatDbTarget,
} from './recordExcelParser';

type Props = {
  parseResult: RecordExcelParseResult;
  uploadContext: RecordExcelUploadDraft & { industryName: string; sourceLabel: string };
  canEditRecords: boolean;
  onReset: () => void;
  onImported?: () => void;
};

function formatCellValue(key: keyof RecordExcelParsedValues, value: unknown): string {
  if (value == null || value === '') return '—';
  if (key === 'baseGrossSalary' || key === 'baseSalaryMonthly') {
    return `${formatMoney(value as number)} ₽`;
  }
  if (key === 'stiTargetPct') return `${value}%`;
  return String(value);
}

function cellClassForLink(link: CompanyLinkResolution | undefined, field: 'company' | 'sector'): string {
  if (!link) return tdClass;
  if (field === 'company' && isCompanyLinkError(link.status)) {
    return `${tdClass} bg-red-50 text-red-800`;
  }
  if (field === 'sector' && (link.status === 'sector_missing' || link.status === 'sector_not_found' || isCompanyLinkError(link.status))) {
    return `${tdClass} bg-red-50 text-red-800`;
  }
  if (link.status === 'matched') {
    return `${tdClass} bg-emerald-50/80`;
  }
  return tdClass;
}

function cellClassForRole(roleResolution: RoleRowResolution | undefined, field: 'family' | 'grade' | 'role'): string {
  if (!roleResolution) return tdClass;
  if (isRoleResolutionError(roleResolution.status)) {
    if (field === 'family' && (roleResolution.status === 'family_missing' || roleResolution.status === 'family_not_found')) {
      return `${tdClass} bg-red-50 text-red-800`;
    }
    if (field === 'grade' && (
      roleResolution.status === 'grade_invalid'
      || roleResolution.status === 'grade_not_found_in_family'
      || roleResolution.status === 'family_missing'
    )) {
      return `${tdClass} bg-red-50 text-red-800`;
    }
    if (field === 'role' && roleResolution.status === 'role_not_found') {
      return `${tdClass} bg-red-50 text-red-800`;
    }
    if (field === 'grade' && roleResolution.status === 'family_not_found') {
      return `${tdClass} bg-red-50 text-red-800`;
    }
  }
  if (roleResolution.status === 'matched_by_name' || roleResolution.status === 'matched_by_family_grade') {
    if (field === 'role' || (field === 'grade' && roleResolution.status === 'matched_by_family_grade')) {
      return `${tdClass} bg-emerald-50/80`;
    }
    if (field === 'family' && roleResolution.status === 'matched_by_family_grade') {
      return `${tdClass} bg-emerald-50/80`;
    }
  }
  return tdClass;
}

function RowDetail({
  row,
  link,
  roleResolution,
}: {
  row: RecordExcelParsedRow;
  link?: CompanyLinkResolution;
  roleResolution?: RoleRowResolution;
}) {
  const valueKeyByLetter: Record<string, keyof RecordExcelParsedValues> = {
    A: 'companyName',
    G: 'sectorName',
    H: 'companyRoleName',
    V: 'datacircleGrade',
    W: 'familyName',
    Z: 'roleName',
    AE: 'baseGrossSalary',
    AO: 'stiTargetPct',
    AX: 'ltipForm',
  };

  const fields = useMemo(() => RECORD_EXCEL_MAPPED_COLUMNS.map((col) => {
    const valueKey = valueKeyByLetter[col.letter];
    let display = formatCellValue(valueKey, row.values[valueKey]);
    if (col.letter === 'AE' && row.values.baseSalaryMonthly != null) {
      display = `${formatCellValue('baseSalaryMonthly', row.values.baseSalaryMonthly)} / мес → ${display} / год`;
    }
    if (col.letter === 'A' && link?.companyId) {
      display += ` → company_id ${link.companyId}`;
    }
    if (col.letter === 'G' && link?.sectorId) {
      display += ` → sector_id ${link.sectorId}`;
    }
    if (col.letter === 'Z' && roleResolution?.roleId) {
      display += ` → role_id ${roleResolution.roleId} (${roleResolution.roleName})`;
    }
    if (col.letter === 'V' && roleResolution?.status === 'matched_by_family_grade' && roleResolution.roleName) {
      display += ` → ${roleResolution.roleName}`;
    }
    return { ...col, display };
  }), [row, link, roleResolution]);

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50/80 px-4 py-3">
      {link && isCompanyLinkError(link.status) && (
        <p className="text-xs text-red-700">{link.message}</p>
      )}
      {roleResolution && isRoleResolutionError(roleResolution.status) && (
        <p className="text-xs text-red-700">{roleResolution.message}</p>
      )}
      {roleResolution && !isRoleResolutionError(roleResolution.status) && roleResolution.status !== 'fallback' && (
        <p className="text-xs text-emerald-800">{roleResolution.message}</p>
      )}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {fields.map((field) => (
          <div
            key={field.letter}
            className={`rounded-md border bg-white px-3 py-2 ${
              (field.letter === 'A' || field.letter === 'G') && link && isCompanyLinkError(link.status)
                ? 'border-red-200 bg-red-50'
                : (field.letter === 'V' || field.letter === 'W' || field.letter === 'Z')
                  && roleResolution
                  && isRoleResolutionError(roleResolution.status)
                  ? 'border-red-200 bg-red-50'
                  : 'border-slate-200'
            }`}
          >
            <div className="text-xs text-dc-muted">
              {field.label}
              <span className="ml-1 text-slate-400">
                ({field.letter} → {formatDbTarget(field.dbTable, field.dbField)})
              </span>
            </div>
            <div className="mt-0.5 text-sm text-slate-800 break-words">{field.display}</div>
          </div>
        ))}
      </div>
      {row.warnings.length > 0 && (
        <ul className="text-xs text-amber-700">
          {row.warnings.map((w) => <li key={w}>• {w}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function RecordExcelUploadPreview({
  parseResult,
  uploadContext,
  canEditRecords,
  onReset,
  onImported,
}: Props) {
  const [expandedRow, setExpandedRow] = useState<number | null>(parseResult.rows[0]?.rowNumber ?? null);
  const [showMapping, setShowMapping] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [companiesInIndustry, setCompaniesInIndustry] = useState<Awaited<ReturnType<typeof fetchCompanies>>>([]);
  const [globalCompanies, setGlobalCompanies] = useState<Awaited<ReturnType<typeof fetchCompanies>>>([]);
  const [sectors, setSectors] = useState<Awaited<ReturnType<typeof fetchSectors>>>([]);
  const [gradingTree, setGradingTree] = useState<GradingTreePayload | null>(null);
  const [overrides, setOverrides] = useState<Record<string, { companyId: number; sectorId: number }>>({});
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ExcelImportResult | null>(null);

  const companyTargets = useMemo(
    () => collectCompanyLinkTargets(parseResult.rows),
    [parseResult.rows],
  );

  const reloadReferenceData = useCallback(async () => {
    setLoadingLinks(true);
    setLinkError(null);
    try {
      const [nextSectors, nextInIndustry, gradingRaw] = await Promise.all([
        fetchSectors(uploadContext.industryId),
        fetchCompanies({ industryId: uploadContext.industryId }),
        fetchGradingTree(),
      ]);
      setSectors(nextSectors);
      setCompaniesInIndustry(nextInIndustry);
      setGradingTree(normalizeGradingTreePayload(gradingRaw));

      const names = Array.from(new Set(companyTargets.map((t) => t.companyName).filter(Boolean)));
      const globalChunks = await Promise.all(
        names.map((name) => fetchCompanies({ search: name })),
      );
      const merged = new Map<number, (typeof nextInIndustry)[number]>();
      for (const list of globalChunks) {
        for (const company of list) merged.set(company.id, company);
      }
      for (const company of nextInIndustry) merged.set(company.id, company);
      setGlobalCompanies(Array.from(merged.values()));
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Не удалось проверить компании');
    } finally {
      setLoadingLinks(false);
    }
  }, [companyTargets, uploadContext.industryId]);

  useEffect(() => {
    reloadReferenceData();
  }, [reloadReferenceData]);

  const companyLinks = useMemo(
    () => resolveCompanyLinks({
      industryId: uploadContext.industryId,
      industryName: uploadContext.industryName,
      sectors,
      companiesInIndustry,
      globalCompanies,
      targets: companyTargets,
      overrides,
    }),
    [companyTargets, companiesInIndustry, globalCompanies, overrides, sectors, uploadContext.industryId, uploadContext.industryName],
  );

  const roleResolutions = useMemo(
    () => (gradingTree ? resolveRoleRows(parseResult.rows, gradingTree) : []),
    [gradingTree, parseResult.rows],
  );

  const unresolvedCount = companyLinks.filter((link) => isCompanyLinkError(link.status)).length;
  const unresolvedRoleCount = roleResolutions.filter((r) => isRoleResolutionError(r.status)).length;
  const rowsWithWarnings = parseResult.rows.filter((r) => r.warnings.length > 0).length;
  const previewRows = parseResult.rows.slice(0, 20);
  const canSave = canEditRecords
    && unresolvedCount === 0
    && unresolvedRoleCount === 0
    && !loadingLinks
    && !importing
    && gradingTree != null;

  const gradeOnlyRoleTargets = useMemo(() => {
    const map = new Map<string, { familyName: string; grade: string; rowNumbers: number[]; resolution?: RoleRowResolution }>();
    for (const row of parseResult.rows) {
      if (row.values.roleName?.trim()) continue;
      if (!row.values.datacircleGrade?.trim()) continue;
      const familyName = row.values.familyName?.trim() || '';
      const grade = row.values.datacircleGrade.trim();
      const key = `${familyName.toLowerCase()}::${grade}`;
      const resolution = roleResolutionForRow(roleResolutions, row.rowNumber);
      const existing = map.get(key);
      if (existing) {
        existing.rowNumbers.push(row.rowNumber);
        continue;
      }
      map.set(key, { familyName, grade, rowNumbers: [row.rowNumber], resolution });
    }
    return Array.from(map.values());
  }, [parseResult.rows, roleResolutions]);

  const handleImport = async () => {
    if (!canSave) return;
    setImporting(true);
    setImportError(null);
    setImportProgress(null);
    try {
      const rows = buildExcelImportRows(parseResult);
      const result = await importExcelRowsInBatches(
        {
          industryId: uploadContext.industryId,
          source: uploadContext.source,
          addedDate: todayIso(),
        },
        rows,
        (done, total) => setImportProgress({ done, total }),
      );
      setImportResult(result);
      if (result.savedCount > 0) {
        onImported?.();
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Не удалось сохранить записи');
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleAddCompany = async (link: CompanyLinkResolution) => {
    if (!canAddCompanyFromExcel(link.status) || !link.sectorId || !link.companyName) return;

    setAddingKey(link.key);
    setAddError(null);
    try {
      const created = await createCompany(
        uploadContext.industryId,
        link.sectorId,
        link.companyName,
      );
      setOverrides((prev) => ({
        ...prev,
        [link.key]: { companyId: created.id, sectorId: link.sectorId! },
      }));
      await reloadReferenceData();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Не удалось добавить компанию');
    } finally {
      setAddingKey(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-800">{parseResult.fileName}</p>
          <p className="mt-1 text-xs text-dc-muted">
            Лист «{parseResult.sheetName}», строк данных: {parseResult.rows.length}
            {rowsWithWarnings > 0 ? ` · предупреждений: ${rowsWithWarnings}` : ''}
            {unresolvedCount > 0 ? ` · компаний без привязки: ${unresolvedCount}` : ''}
            {unresolvedRoleCount > 0 ? ` · ошибок ролей: ${unresolvedRoleCount}` : ''}
          </p>
        </div>
        <button type="button" className={btnLink} onClick={onReset}>Выбрать другой файл</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-dc-muted">Индустрия</div>
          <div className="text-sm font-medium text-slate-800">{uploadContext.industryName}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-dc-muted">Источник</div>
          <div className="text-sm font-medium text-slate-800">{uploadContext.sourceLabel}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-dc-muted">Привязка компаний</div>
          <div className={`text-sm font-medium ${unresolvedCount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            {loadingLinks ? 'Проверка…' : unresolvedCount > 0 ? `${unresolvedCount} требуют внимания` : 'Все найдены'}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-dc-muted">Привязка ролей</div>
          <div className={`text-sm font-medium ${unresolvedRoleCount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            {loadingLinks ? 'Проверка…' : unresolvedRoleCount > 0 ? `${unresolvedRoleCount} ошибок` : 'Все сопоставлены'}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-dc-muted">Данные</div>
          <div className="text-sm font-medium text-slate-800">с строки {parseResult.dataStartRow}</div>
        </div>
      </div>

      {linkError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{linkError}</div>
      )}
      {addError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{addError}</div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Привязка: индустрия → сектор → компания</h3>
        <p className="mb-3 text-xs text-dc-muted">
          Индустрия из шага 1 · сектор из столбца G · компания из столбца A. Записи будут созданы с найденным `company_id`.
        </p>
        <AdminTableWrap>
          <table className="dc-table min-w-[920px] text-sm">
            <thead>
              <tr>
                <th className={thClass}>Строки</th>
                <th className={thClass}>Сектор (G)</th>
                <th className={thClass}>Компания (A)</th>
                <th className={thClass}>Статус</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {companyLinks.map((link) => {
                const isError = isCompanyLinkError(link.status);
                return (
                  <tr key={link.key} className={isError ? 'bg-red-50/60' : undefined}>
                    <td className={tdClass}>{link.rowNumbers.join(', ')}</td>
                    <td className={cellClassForLink(link, 'sector')}>{link.sectorName || '—'}</td>
                    <td className={cellClassForLink(link, 'company')}>{link.companyName || '—'}</td>
                    <td className={`${tdClass} max-w-md text-xs ${isError ? 'text-red-800' : 'text-emerald-800'}`}>
                      {link.message}
                    </td>
                    <td className={tdClass}>
                      {canAddCompanyFromExcel(link.status) && link.sectorId && (
                        <button
                          type="button"
                          className={btnSecondary}
                          disabled={addingKey === link.key || loadingLinks}
                          onClick={() => handleAddCompany(link)}
                        >
                          {addingKey === link.key ? 'Добавление…' : 'Добавить компанию'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </AdminTableWrap>
      </div>

      {gradeOnlyRoleTargets.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Привязка: семья + грейд → роль</h3>
          <p className="mb-3 text-xs text-dc-muted">
            Если роль (Z) не указана, система ищет семью (W), затем роль с грейдом (V) в этой семье. При нескольких ролях на одном грейде выбирается младшая.
          </p>
          <AdminTableWrap>
            <table className="dc-table min-w-[920px] text-sm">
              <thead>
                <tr>
                  <th className={thClass}>Строки</th>
                  <th className={thClass}>Семья (W)</th>
                  <th className={thClass}>Грейд (V)</th>
                  <th className={thClass}>Результат</th>
                </tr>
              </thead>
              <tbody>
                {gradeOnlyRoleTargets.map((target) => {
                  const resolution = target.resolution;
                  const isError = resolution ? isRoleResolutionError(resolution.status) : true;
                  return (
                    <tr key={`${target.familyName}::${target.grade}`} className={isError ? 'bg-red-50/60' : undefined}>
                      <td className={tdClass}>{target.rowNumbers.join(', ')}</td>
                      <td className={cellClassForRole(resolution, 'family')}>{target.familyName || '—'}</td>
                      <td className={cellClassForRole(resolution, 'grade')}>G{target.grade}</td>
                      <td className={`${tdClass} max-w-md text-xs ${isError ? 'text-red-800' : 'text-emerald-800'}`}>
                        {resolution?.message ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AdminTableWrap>
        </div>
      )}

      {parseResult.headerWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Заголовки отличаются от ожидаемых:</p>
          <ul className="mt-1 list-inside list-disc text-xs">
            {parseResult.headerWarnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      <div>
        <button type="button" className={`${btnLink} text-sm`} onClick={() => setShowMapping((v) => !v)}>
          {showMapping ? 'Скрыть' : 'Показать'} схему маппинга Excel → БД
        </button>
        {showMapping && (
          <AdminTableWrap className="mt-2">
            <table className="dc-table min-w-[640px] text-sm">
              <thead>
                <tr>
                  <AdminDevTh label="Excel" className={thClass} />
                  <AdminDevTh label="Поле" className={thClass} />
                  <AdminDevTh label="БД" dbField="*" className={thClass} />
                </tr>
              </thead>
              <tbody>
                {RECORD_EXCEL_MAPPED_COLUMNS.map((col) => (
                  <tr key={col.letter}>
                    <td className={tdClass}>{col.letter}</td>
                    <td className={tdClass}>{col.label}</td>
                    <td className={`${tdClass} font-mono text-xs`}>{formatDbTarget(col.dbTable, col.dbField)}</td>
                  </tr>
                ))}
                {RECORD_EXCEL_CONTEXT_FIELDS.map((field) => (
                  <tr key={`ctx-${field.dbField}`} className="bg-slate-50/80">
                    <td className={tdClass}>—</td>
                    <td className={tdClass}>{field.label}</td>
                    <td className={`${tdClass} font-mono text-xs`}>{formatDbTarget(field.dbTable, field.dbField)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Предпросмотр данных</h3>
        <p className="mb-3 text-xs text-dc-muted">
          Первые {previewRows.length} из {parseResult.rows.length} строк · колонки A, G, H, V, W, Z, AE, AO, AX.
        </p>
        <AdminTableWrap>
          <table className="dc-table min-w-[1200px] text-sm">
            <thead>
              <tr>
                <th className={thClass}>Excel</th>
                {RECORD_EXCEL_COLUMN_PREVIEW.map((col) => (
                  <th key={col.letter} className={thClass} title={`Столбец ${col.letter}`}>{col.label}</th>
                ))}
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row) => {
                const v = row.values;
                const link = companyLinkForRow(companyLinks, v.companyName, v.sectorName);
                const roleResolution = roleResolutionForRow(roleResolutions, row.rowNumber);
                const expanded = expandedRow === row.rowNumber;
                return (
                  <React.Fragment key={row.rowNumber}>
                    <tr className={expanded ? 'bg-dc-primary-soft/30' : undefined}>
                      <td className={tdClass}>{row.rowNumber}</td>
                      <td className={cellClassForLink(link, 'company')}>{v.companyName ?? '—'}</td>
                      <td className={cellClassForLink(link, 'sector')}>{v.sectorName ?? '—'}</td>
                      <td className={tdClass}>{v.companyRoleName ?? '—'}</td>
                      <td className={cellClassForRole(roleResolution, 'grade')}>{v.datacircleGrade ?? '—'}</td>
                      <td className={cellClassForRole(roleResolution, 'family')}>{v.familyName ?? '—'}</td>
                      <td className={cellClassForRole(roleResolution, 'role')}>
                        {v.roleName ?? (roleResolution?.roleName ? `${roleResolution.roleName} (auto)` : '—')}
                      </td>
                      <td className={tdClass}>{v.baseGrossSalary != null ? formatMoney(v.baseGrossSalary) : '—'}</td>
                      <td className={tdClass}>{v.stiTargetPct != null ? `${v.stiTargetPct}%` : '—'}</td>
                      <td className={`${tdClass} max-w-[200px]`}>{v.ltipForm ?? '—'}</td>
                      <td className={tdClass}>
                        <button type="button" className={btnLink} onClick={() => setExpandedRow(expanded ? null : row.rowNumber)}>
                          {expanded ? 'Свернуть' : 'Подробнее'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={RECORD_EXCEL_COLUMN_PREVIEW.length + 2} className="p-0">
                          <RowDetail row={row} link={link} roleResolution={roleResolution} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </AdminTableWrap>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Сохранение в БД</h3>
            <p className="mt-1 text-xs text-dc-muted">
              Будет импортировано {parseResult.rows.length} записей · источник: {uploadContext.sourceLabel}
            </p>
            {!canEditRecords && (
              <p className="mt-1 text-xs text-amber-700">Нет прав на редактирование записей.</p>
            )}
            {unresolvedRoleCount > 0 && (
              <p className="mt-1 text-xs text-red-700">
                Исправьте ошибки сопоставления ролей ({unresolvedRoleCount} строк).
              </p>
            )}
            {unresolvedCount > 0 && (
              <p className="mt-1 text-xs text-red-700">
                Сначала разрешите привязку всех компаний ({unresolvedCount} без совпадения).
              </p>
            )}
          </div>
          <button
            type="button"
            className={btnPrimary}
            disabled={!canSave}
            onClick={handleImport}
          >
            {importing ? (
              importProgress
                ? `Сохранение ${importProgress.done} / ${importProgress.total}…`
                : 'Сохранение…'
            ) : `Сохранить ${parseResult.rows.length} записей`}
          </button>
        </div>

        {importError && (
          <p className="mt-3 text-sm text-red-700">{importError}</p>
        )}

        {importResult && (
          <div className={`mt-3 rounded-md px-3 py-2 text-sm ${importResult.errors.length > 0 ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900'}`}>
            <p className="font-medium">
              Сохранено {importResult.savedCount} из {importResult.total} записей.
            </p>
            {importResult.errors.length > 0 && (
              <ul className="mt-2 list-inside list-disc text-xs">
                {importResult.errors.map((item) => (
                  <li key={`${item.excelRowNumber}-${item.error}`}>
                    Строка Excel {item.excelRowNumber ?? '—'}: {item.error}
                  </li>
                ))}
              </ul>
            )}
            {importResult.errorsTruncated && (
              <p className="mt-2 text-xs">Показаны первые 200 ошибок.</p>
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-dc-muted">
        AE — месячный оклад (в БД годовой ×12). Z — роль по имени; если Z пустой — семья (W) + грейд (V) → role_id (младшая роль на грейде).
      </p>
    </div>
  );
}
